import { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { Shield, Star, ChevronDown, Clock } from 'lucide-react';
import ExamplePageOne from './ExamplePageOne';
import TestScreen2 from './TestScreenTwo';
import React from 'react';
import { ThemeContext } from './ThemeContext';

export default function Landing({loginCallback, className}) {
  const navigate = useNavigate();
  const { setLaunchEmojis } = useContext(ThemeContext);
  const [isVisible, setIsVisible] = useState(false);
  const [noiseUrl, setNoiseUrl] = useState('');

  useEffect(() => {
    const dpr = window.devicePixelRatio || 1;
    const size = Math.round(256 * dpr);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.createImageData(size, size);
    for (let i = 0; i < imageData.data.length; i += 4) {
      const v = Math.random() * 255;
      imageData.data[i] = v;
      imageData.data[i + 1] = v;
      imageData.data[i + 2] = v;
      imageData.data[i + 3] = 255;
    }
    ctx.putImageData(imageData, 0, 0);
    setNoiseUrl(canvas.toDataURL());
  }, []); 
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [score, setScore] = useState(0);
  const [currentPage, setCurrentPage] = useState('home');
  const [showDemoButton, setShowDemoButton] = useState(true);


  const [currentIndex, setCurrentIndex] = useState(0);
  const totalSlides = 2;
  
  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {

    // Start counter animation after a delay
    const counterDelay = setTimeout(() => {
      let currentScore = 0;
      const targetScore = 44;
      
      const animateScore = () => {
        currentScore++;
        setScore(currentScore);
        
        if (currentScore >= targetScore) {
          return;   
        }
        
        // Calculate delay - starts fast, slows down as it approaches the target
        const progress = currentScore / targetScore;
        const baseDelay = 30;
        const slowdownFactor = Math.pow(progress, 2) * 200; // Exponential slowdown
        const delay = baseDelay + slowdownFactor;
        
        setTimeout(animateScore, delay);
        
      };
      
      animateScore();
    }, 1500); // Start after 1.5s delay
    
    return () => {
      clearTimeout(counterDelay);

    };
  }, []);

  useEffect(() => {
    if (showEmailModal) {
      setEmail('');
      setNotification({ show: false, type: '', message: '' });
    }
  }, [showEmailModal]);

  useEffect(() => {

      const repeat_slide = setInterval(()=>{

      setCurrentIndex((prev) => (prev + 1) % totalSlides);
      }, 10000)

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      const scrollableHeight = documentHeight - windowHeight;
      const isNearBottom = scrollTop >= (scrollableHeight * 0.75);
      setShowDemoButton(!isNearBottom);
    };

    window.addEventListener('scroll', handleScroll);
    return () =>{
      window.removeEventListener('scroll', handleScroll)
      clearInterval(repeat_slide)
    };
  }, []);

  // Logo Component
  const Logo = () => (
      <div className="flex flex-col">
        <img src="hingelogo.png" className="h-4 lg:h-6 w-auto object-contain"/>
      </div>
  );

  const { innerWidth: width, innerHeight: height } = window;

  

  return (
    <>
      {/* Noisy gradient background */}
      <div className="fixed inset-0 -z-10" style={{ background: 'radial-gradient(ellipse at bottom right, #7c3aed 0%, #7c3aed22 70%, transparent 90%), radial-gradient(ellipse 70% 70% at top left, #ec4899 0%, #ec489922 65%, transparent 90%), #f5f5f5' }}>
        {noiseUrl && (
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${noiseUrl})`,
            backgroundRepeat: 'repeat',
            backgroundSize: '256px 256px',
            mixBlendMode: 'soft-light',
            opacity: 0.35
          }}/>
        )}
      </div>

      {/* Main Content */}
      <div className={`${className} transition-all duration-500 ${showEmailModal ? 'blur-sm scale-[0.98]' : 'blur-0 scale-100'} overflow-x-hidden`}>
        <div className="max-w-6xl mx-auto w-full min-h-screen lg:min-h-0 lg:h-auto">
          {/* Navigation Header */}
          <nav className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 lg:p-6 z-30">
                <Link className={`font-medium text-xs transition-colors text-gray-600 hover:text-purple-70'}`} to="/">
              <Logo/>
            </Link>
            <a href="mailto:hello@hingemax.com" className="font-medium text-xs transition-colors text-gray-600 hover:text-purple-700">
              Contact
            </a>
          </nav>
          
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center lg:min-h-screen pt-16 lg:pt-20">
            {/* Left Side - Content */}
            <div className="text-center lg:text-left space-y-4 lg:space-y-6 px-4 sm:px-6 lg:px-2 order-1 lg:order-1 flex flex-col justify-center min-h-[calc(100vh-120px)] lg:min-h-0 relative z-20">
              {/* Main Headline */}
              <h1 className={`text-2xl sm:text-3xl md:text-3xl lg:text-4xl font-black text-gray-900 leading-tight tracking-tight transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                Spotify Wrapped, but for your{' '}
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-pink-900 via-pink-700 to-pink-900 bg-clip-text text-transparent">
                  Hinge
                  </span>
                  <div className="absolute -inset-1 bg-gradient-to-r from-purple-900/20 to-purple-700/20 blur-lg rounded-lg -z-10"></div>
                </span>
                {' '}profile  💘.
              </h1>

              {/* Subtitle */}
              <p className={`text-sm sm:text-base text-gray-600 max-w-lg mx-auto lg:mx-0 font-serif italic transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
              Instant analytics of every conversation you've ever had. See what your profile says about yourself, completely free.
              </p>

              {/* Feature Pills */}
              <div className={`flex flex-wrap justify-center gap-2 lg:justify-start lg:gap-3 pt-2 lg:pt-3 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
                  <Shield className="w-4 h-4 text-black" />
                  <span className="text-[10px] sm:text-xs font-medium text-gray-700">Secure Hinge Login</span>
                </div>
                <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 delay-75">
                  <Star className="w-4 h-4 text-black" />
                  <span className="text-[10px] sm:text-xs font-medium text-gray-700">Free Analysis</span>
                </div>
                <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 delay-200">
                  <Clock className="w-4 h-4 text-black" />
                  <span className="text-[10px] sm:text-xs font-medium text-gray-700">Results in 30 seconds</span>
                </div>
              </div>

              {/* CTA Section - no animation */}
              <div className="pt-3 lg:pt-4">
                <button
                  onTouchEnd={(e) => { e.preventDefault(); setLaunchEmojis(true); navigate('/AuthFlow'); }}
                  onClick={() => { setLaunchEmojis(true); navigate('/AuthFlow'); }}
                  className="transition-transform duration-100 active:scale-95"
                  style={{ padding: '12px 32px', background: '#831843', color: '#fff', borderRadius: '16px', fontWeight: '700', fontSize: '14px', border: 'none', cursor: 'pointer', position: 'relative', zIndex: 9999, touchAction: 'manipulation' }}
                >
                  Get My Stats Now
                </button>
                <p className="text-[10px] text-gray-500 mt-2">
                  Connect with Hinge • Free analysis • No spam
                </p>
              </div>
            </div>

               <div className={showDemoButton ? `opacity-0 pointer-events-none transition-opacity duration-500 absolute top-[52%] w-[80vw] left-1/2 -translate-x-1/2 -translate-y-1/2` : `transition-opacity duration-500 opacity-100 absolute top-[52%] w-[90vw] left-1/2 -translate-x-1/2 -translate-y-1/2`}>
               
                <p className='text-center text-xl py-6 font-bold bold italic'>Login to see your...</p>

                <div
  className="w-[80vw] overflow-hidden mx-auto"
  style={{ whiteSpace: 'nowrap' }}
>
  <div
    className="inline-block animate-scroll-left"
    style={{
      display: 'inline-block',
      animation: 'scroll-left 20s linear infinite',
    }}
  >
    <span className="inline-block text-center font-serif italic mr-4">Message Count vs Others 💌</span>
    <span className="inline-block text-center font-serif italic mr-4">Psychological Chat Analysis ❤️</span>
    <span className="inline-block text-center font-serif italic mr-4">Message Length/Time vs Others 💘</span>
    <span className="inline-block text-center font-serif italic mr-4">Tone trends in failed conversations 💔</span>
    <span className="inline-block text-center font-serif italic">+Much More 💖</span>
  </div>

  {/* Inline <style> block for animation */}
  <style jsx>{`
    @keyframes scroll-left {
      0% {
        transform: translateX(0%);
      }
      100% {
        transform: translateX(-50%);
      }
    }
  `}</style>
</div>


               </div>   

            {/* Right Side - Browser Mockup */}
<div className={`relative px-4 sm:px-6 lg:px-2 order-2 lg:order-2 transition-all duration-1200 delay-300 pb-8 lg:pb-0 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
  <div className="absolute inset-0 bg-gradient-to-br from-purple-400/30  via-pink-300/20 to-blue-400/30 rounded-2xl blur-xl animate-pulse"></div>
  <div
    className="relative backdrop-blur-3xl bg-white/20 border border-white/30 rounded-2xl shadow-2xl p-1.5 w-full max-w-[80vw] sm:max-w-xl lg:max-w-md mx-auto transition-all duration-700 hover:shadow-3xl"
    style={{
      transform: isVisible ? 'perspective(1000px) rotateY(-5deg) rotateX(2deg)' : 'perspective(1000px) rotateY(-20deg) rotateX(5deg) translateX(50px)',
      transformStyle: 'preserve-3d'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'perspective(1000px) rotateY(0deg) rotateX(0deg) scale(1.02)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'perspective(1000px) rotateY(-5deg) rotateX(2deg) scale(1)';
    }}
  >
    {/* Browser Header */}
    <div className="flex items-center gap-2 px-4 py-2.5 bg-white/30 backdrop-blur-2xl rounded-t-xl border-b border-white/20">
      <div className="flex gap-1">
        <div className="w-2.5 h-2.5 bg-red-400 rounded-full hover:scale-110 transition-transform"></div>
        <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full hover:scale-110 transition-transform"></div>
        <div className="w-2.5 h-2.5 bg-green-400 rounded-full hover:scale-110 transition-transform"></div>
      </div>
      <div className="flex-1 mx-3">
        <div className="bg-white/40 backdrop-blur-sm rounded-lg px-3 py-1 text-[10px] text-gray-600 font-mono border border-white/20">
          hingemax.com
        </div>
      </div>
    </div>
    {/* App Content - sliding carousel */}
    <div className="relative overflow-hidden flex flex-col rounded-b-2xl" style={{ height: '420px' }}>
      <div
        className="flex flex-1 transition-transform duration-700 ease-in-out"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        <div className="flex-shrink-0 h-full overflow-y-auto" style={{ width: '100%' }}>
          <ExamplePageOne/>
        </div>
        <div className="flex-shrink-0 h-full overflow-y-auto" style={{ width: '100%' }}>
          <TestScreen2/>
        </div>
      </div>

      {/* Dot indicators */}
      <div className="flex justify-center gap-1.5 py-2.5 shrink-0">
        {[...Array(totalSlides)].map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentIndex(i)}
            className={`rounded-full transition-all duration-300 ${
              i === currentIndex
                ? 'w-4 h-1.5 bg-pink-700'
                : 'w-1.5 h-1.5 bg-gray-300 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>
    </div>
  </div>
</div>
          </div>
          
        </div>
      </div>

      {/* Mobile Demo Indicator - Only visible on mobile */}

      <div className={`lg:hidden fixed bottom-8 left-1/2 transform -translate-x-1/2 z-40 transition-opacity duration-500 ${showDemoButton ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <div className="relative">
          {/* Background glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-400/30 via-pink-300/20 to-blue-400/30 rounded-full blur-xl animate-pulse"></div>
          
          {/* Main glass container */}
          <div 
            className="relative flex items-center gap-2 bg-white/20 backdrop-blur-2xl border border-white/40 rounded-full px-5 py-3 shadow-2xl animate-bounce cursor-pointer hover:scale-105 transition-transform duration-300" 
            style={{
              animationDuration: '2s'
            }}
            onClick={() => {
              window.scrollTo({
                top: document.documentElement.scrollHeight,
                behavior: 'smooth'
              });
            }}
          >
            {/* Inner gradient overlay for liquid effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-white/5 rounded-full"></div>
            
            {/* Shine effect */}
            <div className="absolute top-1 left-1 right-1 h-1/2 bg-gradient-to-b from-white/40 to-transparent rounded-full blur-sm"></div>
            
            {/* Content */}
            <span className="relative text-xs font-medium text-gray-600 drop-shadow-lg">View Demo</span>
            <ChevronDown className="relative w-3 h-3 text-gray-600 drop-shadow-lg" />
          </div>
        </div>
      </div>
    </>
  );
}